import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { SnackbarService } from '@shared/services/snackbar.service';
import { TokenResponse } from '@shared/models/TokenResponse.model';
import { UserService } from './user-service.service';
import { AuthApiService } from './api/auth.api.service';

@Injectable({
  providedIn: 'root' // service to work with auth flow and credentials
})
export class AuthService {

  constructor(
    private api: AuthApiService,
    private userService: UserService,
    private snackbar: SnackbarService,
    private router: Router
  ) { }

  public routeBeforeGuard: string;
  private timeout: any;

  public errorHandler(error: HttpErrorResponse): Observable<HttpEvent<any>> {
    this.logout();
    this.snackbar.error('Unauthorized, please login again');
    throw error;
  }

  public wrongCredentials(error: HttpErrorResponse): Observable<HttpEvent<any>> {
    this.snackbar.error('Please try again');
    throw error;
  }

  public setToken(payload: TokenResponse): void {
    localStorage.setItem('access_token', payload.access_token);
    localStorage.setItem('refresh_token', payload.refresh_token);
    localStorage.setItem('expire', (new Date().getTime() + payload.expires_in * 1000).toString());
    this.timeout = setTimeout(() => {
      this.refreshToken(payload.refresh_token);
    }, 59 * 60 * 1000);
  }

  public scheduleTokenRefresh(): void {
    clearTimeout(this.timeout); // clearing timeout in case of use change
    const expiryTimestamp = this.getTokenExpiryTimestamp();
    const token = this.getRefreshToken();
    const difference = expiryTimestamp - new Date().getTime();
    if (new Date().getTime() > expiryTimestamp) { // logout in case token is expired, this is impossible case but still
      this.logout();
      return;
    }
    if (expiryTimestamp && token) {
      if (difference < 60000) {
        this.refreshToken(token); // refresh immediately when expiry time is less than one minute.
      } else {
        this.timeout = setTimeout(() => {
          this.refreshToken(token); // schedule refresh on: time left - one minute
        }, difference - 60000);
      }
    }
  }

  public refreshToken(token: string): void {
    this.api.refresh(token)
      .pipe(take(1))
      .subscribe(data => {
       this.setToken(data);
      });
  }

  public setRouteBeforeGuard(route: string): void {
    this.routeBeforeGuard = route; // remember user route before he was logged in;
  }

  public getAccessToken(): string {
    return localStorage.getItem('access_token');
  }

  public getRefreshToken(): string {
    return localStorage.getItem('refresh_token');
  }

  public getTokenExpiryTimestamp(): number {
    return +(localStorage.getItem('expire'));
  }

  public logout(): void {
    localStorage.clear();
    sessionStorage.clear();
    clearTimeout(this.timeout);
    this.routeBeforeGuard = null;
    this.userService.setUser(null);
    this.router.navigate(['login']);
  }

}
